import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { Alert } from '../../../../components/Alert';
import { Button } from '../../../../components/Button';
import { PageSpinner } from '../../../../components/PageSpinner';
import { StatusBadge } from '../../../../components/StatusBadge';
import { useAuth } from '../../../../features/auth/auth-context';
import { billingApi } from '../../../../features/billing/billing.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatBusinessDate } from '../../../../lib/format-date';
import { paths, routeTo } from '../../../../routes/paths';
import styles from '../AdminPortal.module.css';

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

export function RepaymentDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const repayment = useQuery({
    queryKey: ['admin', 'billing', 'repayments', id],
    queryFn: () => billingApi.getAdminRepayment(id),
  });

  const sendAuthorization = useMutation({
    mutationFn: () => billingApi.sendAuthorization(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'billing', 'repayments', id] });
    },
  });

  if (repayment.isPending) {
    return <PageSpinner label="Loading repayment schedule" />;
  }

  if (repayment.isError) {
    return <Alert tone="error">{toDisplayMessage(repayment.error)}</Alert>;
  }

  const data = repayment.data;
  const canSendAuthorization = can(PERMISSIONS.BILLING_REMINDERS_SEND);
  const mutationError = sendAuthorization.error ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Instalment #{data.sequenceNumber}</h1>
        <StatusBadge status={data.status} />
      </header>

      <p>
        <Link to={paths.adminBilling}>Back to billing</Link>
      </p>

      {mutationError !== null && <Alert tone="error">{toDisplayMessage(mutationError)}</Alert>}
      {sendAuthorization.isSuccess && (
        <Alert tone="success">
          Authorization link sent. Link expires{' '}
          {formatTimestamp(sendAuthorization.data.linkExpiresAt)}.
        </Alert>
      )}

      <section className={styles.card} aria-labelledby="repayment-details-heading">
        <h2 className={styles.cardTitle} id="repayment-details-heading">
          Schedule details
        </h2>

        <dl className={styles.definitionList}>
          <dt>Schedule ID</dt>
          <dd>{data.id}</dd>
          <dt>Contract</dt>
          <dd>
            <Link to={routeTo.adminApplication(data.subscriptionId)}>
              {data.contractReference ?? data.subscriptionId.slice(0, 8)}
            </Link>
          </dd>
          <dt>Product</dt>
          <dd>{data.productName ?? '—'}</dd>
          <dt>Due date</dt>
          <dd>{formatBusinessDate(data.dueDate)}</dd>
          <dt>Instalment amount</dt>
          <dd>{formatNgn(data.instalmentAmount)}</dd>
          <dt>Amount paid</dt>
          <dd>{formatNgn(data.amountPaid)}</dd>
          <dt>Paid at</dt>
          <dd>{formatTimestamp(data.paidAt)}</dd>
          <dt>Overdue at</dt>
          <dd>{formatTimestamp(data.overdueAt)}</dd>
          <dt>Active payment attempt</dt>
          <dd>
            {data.activePaymentAttemptId === null ? (
              '—'
            ) : (
              <Link to={routeTo.adminPaymentAttempt(data.activePaymentAttemptId)}>
                {data.activePaymentAttemptId.slice(0, 8)}
              </Link>
            )}
          </dd>
        </dl>

        {canSendAuthorization &&
          (data.status === 'DUE' ||
            data.status === 'AWAITING_CUSTOMER_AUTHORIZATION' ||
            data.status === 'OVERDUE') && (
            <div className={styles.actions}>
              <Button busy={sendAuthorization.isPending} onClick={() => sendAuthorization.mutate()}>
                Send authorization link
              </Button>
            </div>
          )}
      </section>
    </div>
  );
}
